/**
 * AudioScriptApi — the `engine.audio` capability module (global service).
 *
 * Two surfaces: (1) fire-and-forget one-shots by clip GUID (`playOneShot`), and (2) per-source
 * control of an AudioSource ENTITY's own tracked voice (`play`/`stop`/`pause`/`resume`) so a
 * script can start AND stop a specific source. The backing is the runtime AudioSubsystem
 * (entity→GUID→voice), injected per runtime (DI). Clips must already be loaded; missing → no-op.
 */

import { scriptApi, scriptFn } from "../scriptApi";
import type { EntityHandle } from "../IEngineContext";

/** The injected audio backing (AudioSubsystem-backed at runtime; DI per runtime). */
export interface AudioBacking {
  /** Fire-and-forget one-shot of a (preloaded) clip by GUID, optional [0,1] volume scale. */
  playOneShot(clipGuid: string, volume?: number): void;
  /** (Re)start this AudioSource entity's own clip as a tracked voice. */
  play(entity: EntityHandle): void;
  /** Stop this AudioSource entity's voice. */
  stop(entity: EntityHandle): void;
  /** Pause this AudioSource entity's voice. */
  pause(entity: EntityHandle): void;
  /** Resume this AudioSource entity's paused voice. */
  resume(entity: EntityHandle): void;
  /** Stop every currently-playing voice. */
  stopAll(): void;
}

@scriptApi("audio", { tier: "safe", version: 2 })
export class AudioScriptApi {
  constructor(private readonly _backing: AudioBacking) {}

  /** Play a (preloaded) clip one-shot, optionally at a [0,1] volume scale. */
  @scriptFn() playOneShot(clipGuid: string, volume?: number): void {
    this._backing.playOneShot(clipGuid, volume);
  }

  /** (Re)start an AudioSource entity's own clip as a tracked voice (stoppable). */
  @scriptFn() play(entity: EntityHandle): void {
    this._backing.play(entity);
  }

  /** Stop an AudioSource entity's voice. */
  @scriptFn() stop(entity: EntityHandle): void {
    this._backing.stop(entity);
  }

  /** Pause an AudioSource entity's voice. */
  @scriptFn() pause(entity: EntityHandle): void {
    this._backing.pause(entity);
  }

  /** Resume an AudioSource entity's paused voice. */
  @scriptFn() resume(entity: EntityHandle): void {
    this._backing.resume(entity);
  }

  /** Stop every currently-playing voice. */
  @scriptFn() stopAll(): void {
    this._backing.stopAll();
  }
}
